import * as React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useI18n } from "../../i18n/I18nProvider";

type Props = {
  handlePageChange: (page: string) => void;
  nextPage?: string;
  backPage?: string;
};

export default function AwareCrisisVideoScreen({
  handlePageChange,
  nextPage = "AwareStayOrLeave",
  backPage = "VibeQuestions:aware",
}: Props) {
  const { lang } = useI18n();
  const isDE = lang === "de";

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `
          linear-gradient(rgba(8,8,8,0.78), rgba(8,8,8,0.84)),
          url("/scenarioBgs/leaveOrstay.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: 5,
          p: { xs: 4, sm: 6, md: 7 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49,32,32,0.95), rgba(34,21,21,0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        <Stack spacing={4} alignItems="center">
          <PlayCircleOutlineIcon sx={{ fontSize: 56, color: "#d6293e" }} />

          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.35rem" },
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              lineHeight: 1.25,
            }}
          >
            {isDE ? "Achte auf das Video" : "Pay attention to the video"}
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.84)",
              fontSize: { xs: "1rem", sm: "1.15rem" },
              fontWeight: 500,
              lineHeight: 1.65,
              maxWidth: 650,
            }}
          >
            {isDE
              ? "Du hast die Awareness-Fragen abgeschlossen. Gleich zeigt die Workshop-Leitung im Lab ein Video. Schau aufmerksam zu, denn danach triffst du deine nächste Entscheidung."
              : "You have completed the awareness questions. The workshop host will now play a video in the lab. Watch closely, because your next decision follows right after it."}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="outlined"
              onClick={() => handlePageChange(backPage)}
              sx={{
                mt: 1,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                px: 4,
                py: 1.25,
                borderColor: "rgba(255,255,255,0.4)",
                color: "#fff",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              {isDE ? "Zurück" : "Back"}
            </Button>

            <Button
              variant="contained"
              onClick={() => handlePageChange(nextPage)}
              sx={{
                mt: 1,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                px: 4,
                py: 1.25,
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                "&:hover": {
                  background: "linear-gradient(90deg, #9f0b1c, #c32136)",
                },
              }}
            >
              {isDE ? "Weiter" : "Continue"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
